import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { ConversationAgent, Conversation, ConversationAgentPhase } from '../../../shared/types-conversation';

const ACCENT = '#f97316';

const STATUS_COLORS: Record<string, string> = {
  active: '#10b981',
  paused: '#f59e0b',
  disabled: '#6b7280',
  waiting: '#3b82f6',
  approval_pending: '#f59e0b',
  completed: '#10b981',
  failed: '#ef4444',
  expired: '#6b7280',
};

type View = 'list' | 'builder' | 'conversations' | 'detail';

interface PhaseDraft {
  id: string;
  name: string;
  action: string;
  wait_for_type: 'none' | 'approval' | 'event' | 'timeout';
  wait_for_config: string;
  next_phase: string;
  on_timeout_phase: string;
}

export default function ConversationAgentsPage() {
  const [agents, setAgents] = useState<ConversationAgent[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConv, setSelectedConv] = useState<Conversation | null>(null);
  const [view, setView] = useState<View>('list');
  const [loading, setLoading] = useState(true);
  const [editingAgent, setEditingAgent] = useState<ConversationAgent | null>(null);

  // Builder state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [triggerType, setTriggerType] = useState<'manual' | 'bus_event' | 'schedule'>('manual');
  const [triggerConfig, setTriggerConfig] = useState('');
  const [phases, setPhases] = useState<PhaseDraft[]>([]);
  const [timeoutHours, setTimeoutHours] = useState(24);
  const [escalationEnabled, setEscalationEnabled] = useState(false);
  const [escalateAfterHours, setEscalateAfterHours] = useState(4);
  const [escalateTo, setEscalateTo] = useState('');
  const [agentStatus, setAgentStatus] = useState<'active' | 'paused' | 'disabled'>('active');

  useEffect(() => { loadAgents(); }, []);

  async function loadAgents() {
    setLoading(true);
    try {
      const res = await api.get<{ agents: ConversationAgent[] }>('/conversation-agents');
      setAgents(res.agents);
    } catch { /* ignore */ }
    setLoading(false);
  }

  async function loadConversations(agentId?: string) {
    try {
      const url = agentId ? `/conversations?agent_id=${agentId}` : '/conversations';
      const res = await api.get<{ conversations: Conversation[] }>(url);
      setConversations(res.conversations);
    } catch { /* ignore */ }
  }

  function openBuilder(agent?: ConversationAgent) {
    if (agent) {
      setEditingAgent(agent);
      setName(agent.name);
      setDescription(agent.description);
      setTriggerType(agent.trigger.type);
      setTriggerConfig(JSON.stringify(agent.trigger.config));
      setPhases(agent.phases.map(p => ({
        id: p.id,
        name: p.name,
        action: p.action,
        wait_for_type: p.wait_for ? p.wait_for.type : 'none',
        wait_for_config: p.wait_for ? JSON.stringify(p.wait_for.config) : '{}',
        next_phase: p.next_phase || '',
        on_timeout_phase: p.on_timeout_phase || '',
      })));
      setTimeoutHours(agent.timeout_hours);
      setEscalationEnabled(agent.escalation.enabled);
      setEscalateAfterHours(agent.escalation.escalate_after_hours);
      setEscalateTo(agent.escalation.escalate_to);
      setAgentStatus(agent.status);
    } else {
      setEditingAgent(null);
      setName('');
      setDescription('');
      setTriggerType('manual');
      setTriggerConfig('{}');
      setPhases([]);
      setTimeoutHours(24);
      setEscalationEnabled(false);
      setEscalateAfterHours(4);
      setEscalateTo('');
      setAgentStatus('active');
    }
    setView('builder');
  }

  function addPhase() {
    setPhases([...phases, {
      id: crypto.randomUUID(),
      name: '',
      action: '',
      wait_for_type: 'none',
      wait_for_config: '{}',
      next_phase: '',
      on_timeout_phase: '',
    }]);
  }

  function updatePhase(index: number, field: string, value: string) {
    const updated = [...phases];
    (updated[index] as unknown as Record<string, unknown>)[field] = value;
    setPhases(updated);
  }

  function removePhase(index: number) {
    setPhases(phases.filter((_, i) => i !== index));
  }

  function movePhase(index: number, direction: -1 | 1) {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= phases.length) return;
    const updated = [...phases];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setPhases(updated);
  }

  async function saveAgent() {
    let config: Record<string, unknown> = {};
    try { config = JSON.parse(triggerConfig); } catch { /* */ }

    const payload = {
      name,
      description,
      trigger: { type: triggerType, config },
      phases: phases.map(p => {
        const phase: ConversationAgentPhase = { id: p.id, name: p.name, action: p.action };
        if (p.wait_for_type !== 'none') {
          let wfConfig: Record<string, unknown> = {};
          try { wfConfig = JSON.parse(p.wait_for_config); } catch { /* */ }
          phase.wait_for = { type: p.wait_for_type as 'approval' | 'event' | 'timeout', config: wfConfig };
        }
        if (p.next_phase) phase.next_phase = p.next_phase;
        if (p.on_timeout_phase) phase.on_timeout_phase = p.on_timeout_phase;
        return phase;
      }),
      timeout_hours: timeoutHours,
      escalation: { enabled: escalationEnabled, escalate_after_hours: escalateAfterHours, escalate_to: escalateTo },
      status: agentStatus,
    };

    try {
      if (editingAgent) {
        await api.patch(`/conversation-agents/${editingAgent.id}`, payload);
      } else {
        await api.post('/conversation-agents', payload);
      }
      await loadAgents();
      setView('list');
    } catch { /* ignore */ }
  }

  async function deleteAgent(id: string) {
    if (!confirm('Delete this agent?')) return;
    try {
      await api.delete(`/conversation-agents/${id}`);
      await loadAgents();
    } catch { /* ignore */ }
  }

  async function startConversation(agentId: string) {
    try {
      await api.post(`/conversation-agents/${agentId}/start`, {});
      await loadAgents();
    } catch { /* ignore */ }
  }

  async function viewConversations(agentId?: string) {
    await loadConversations(agentId);
    setView('conversations');
  }

  async function viewConversationDetail(convId: string) {
    try {
      const conv = await api.get<Conversation>(`/conversations/${convId}`);
      setSelectedConv(conv);
      setView('detail');
    } catch { /* ignore */ }
  }

  async function approveConversation(id: string) {
    try {
      await api.post(`/conversations/${id}/approve`, {});
      await viewConversationDetail(id);
    } catch { /* ignore */ }
  }

  async function rejectConversation(id: string) {
    try {
      await api.post(`/conversations/${id}/reject`, {});
      await viewConversationDetail(id);
    } catch { /* ignore */ }
  }

  async function cancelConversation(id: string) {
    if (!confirm('Cancel this conversation?')) return;
    try {
      await api.post(`/conversations/${id}/cancel`, {});
      await viewConversationDetail(id);
    } catch { /* ignore */ }
  }

  if (loading) return <p>Loading...</p>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>Conversation Agents</h1>
          <p style={{ color: '#6b7280', margin: '0.25rem 0 0', fontSize: '0.875rem' }}>Long-running stateful agents with phases, approvals, and escalation</p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {view !== 'list' && (
            <button onClick={() => { setView('list'); setSelectedConv(null); }} style={btnStyle('#6b7280')}>Back</button>
          )}
          {view === 'list' && (
            <>
              <button onClick={() => viewConversations()} style={btnStyle('#8b5cf6')}>All Conversations</button>
              <button onClick={() => openBuilder()} style={btnStyle(ACCENT)}>+ New Agent</button>
            </>
          )}
        </div>
      </div>

      {view === 'list' && renderList()}
      {view === 'builder' && renderBuilder()}
      {view === 'conversations' && renderConversations()}
      {view === 'detail' && selectedConv && renderDetail()}
    </div>
  );

  function renderList() {
    if (agents.length === 0) {
      return (
        <div style={emptyStyle}>
          <p style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0 }}>No conversation agents</p>
          <p style={{ color: '#6b7280', margin: '0.5rem 0' }}>Create a conversation agent to orchestrate multi-step workflows.</p>
          <button onClick={() => openBuilder()} style={btnStyle(ACCENT)}>+ New Agent</button>
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {agents.map(a => (
          <div key={a.id} style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                  <span style={{ fontWeight: 600 }}>{a.name}</span>
                  <span style={badgeStyle(STATUS_COLORS[a.status])}>{a.status}</span>
                  <span style={badgeStyle('#3b82f6')}>{a.trigger.type}</span>
                </div>
                {a.description && <p style={{ color: '#6b7280', fontSize: '0.8125rem', margin: '0.25rem 0' }}>{a.description}</p>}
                <div style={{ color: '#9ca3af', fontSize: '0.75rem', display: 'flex', gap: '1rem' }}>
                  <span>{a.phases.length} phase{a.phases.length !== 1 ? 's' : ''}</span>
                  <span>{a.active_conversations} active</span>
                  <span>Timeout: {a.timeout_hours}h</span>
                  {a.escalation.enabled && <span>Escalates after {a.escalation.escalate_after_hours}h</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.375rem' }}>
                <button onClick={() => startConversation(a.id)} style={btnSmall('#10b981')} title="Start">Start</button>
                <button onClick={() => viewConversations(a.id)} style={btnSmall('#8b5cf6')} title="View">Convos</button>
                <button onClick={() => openBuilder(a)} style={btnSmall('#6b7280')} title="Edit">Edit</button>
                <button onClick={() => deleteAgent(a.id)} style={btnSmall('#ef4444')} title="Delete">Del</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  function renderBuilder() {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div style={cardStyle}>
          <h2 style={{ fontSize: '1.125rem', fontWeight: 600, margin: '0 0 1rem' }}>
            {editingAgent ? 'Edit Agent' : 'New Agent'}
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={labelStyle}>Name</label>
              <input value={name} onChange={e => setName(e.target.value)} style={inputStyle} placeholder="Onboarding workflow" />
            </div>
            <div>
              <label style={labelStyle}>Status</label>
              <select value={agentStatus} onChange={e => setAgentStatus(e.target.value as 'active' | 'paused' | 'disabled')} style={inputStyle}>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} placeholder="What does this agent do?" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={labelStyle}>Trigger Type</label>
              <select value={triggerType} onChange={e => setTriggerType(e.target.value as 'manual' | 'bus_event' | 'schedule')} style={inputStyle}>
                <option value="manual">Manual</option>
                <option value="bus_event">Bus Event</option>
                <option value="schedule">Schedule</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Trigger Config (JSON)</label>
              <input value={triggerConfig} onChange={e => setTriggerConfig(e.target.value)} style={inputStyle} placeholder='{"event_type": "..."}' />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={labelStyle}>Timeout (hours)</label>
              <input type="number" value={timeoutHours} onChange={e => setTimeoutHours(parseInt(e.target.value) || 24)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Escalation</label>
              <select value={escalationEnabled ? 'yes' : 'no'} onChange={e => setEscalationEnabled(e.target.value === 'yes')} style={inputStyle}>
                <option value="no">Disabled</option>
                <option value="yes">Enabled</option>
              </select>
            </div>
            {escalationEnabled && (
              <>
                <div>
                  <label style={labelStyle}>Escalate After (hours)</label>
                  <input type="number" value={escalateAfterHours} onChange={e => setEscalateAfterHours(parseInt(e.target.value) || 4)} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Escalate To</label>
                  <input value={escalateTo} onChange={e => setEscalateTo(e.target.value)} style={inputStyle} placeholder="admin@example.com" />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Phases */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>Phases ({phases.length})</h3>
            <button onClick={addPhase} style={btnStyle(ACCENT)}>+ Add Phase</button>
          </div>

          {phases.length === 0 && (
            <p style={{ color: '#9ca3af', textAlign: 'center', padding: '2rem 0' }}>No phases. Click &quot;+ Add Phase&quot; to begin.</p>
          )}

          {phases.map((phase, i) => (
            <div key={phase.id} style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '0.75rem', marginBottom: '0.5rem', background: '#fafafa' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: ACCENT, minWidth: 20 }}>#{i + 1}</span>
                <input value={phase.name} onChange={e => updatePhase(i, 'name', e.target.value)} style={{ ...inputStyle, flex: 1 }} placeholder="Phase name" />
                <button onClick={() => movePhase(i, -1)} style={btnSmall('#6b7280')} disabled={i === 0}>&#9650;</button>
                <button onClick={() => movePhase(i, 1)} style={btnSmall('#6b7280')} disabled={i === phases.length - 1}>&#9660;</button>
                <button onClick={() => removePhase(i)} style={btnSmall('#ef4444')}>X</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px', gap: '0.5rem', paddingLeft: 28, marginBottom: '0.5rem' }}>
                <input value={phase.action} onChange={e => updatePhase(i, 'action', e.target.value)} style={inputStyle} placeholder="Action description" />
                <select value={phase.wait_for_type} onChange={e => updatePhase(i, 'wait_for_type', e.target.value)} style={inputStyle}>
                  <option value="none">No wait</option>
                  <option value="approval">Wait: Approval</option>
                  <option value="event">Wait: Event</option>
                  <option value="timeout">Wait: Timeout</option>
                </select>
              </div>
              {phase.wait_for_type !== 'none' && (
                <div style={{ paddingLeft: 28 }}>
                  <input value={phase.wait_for_config} onChange={e => updatePhase(i, 'wait_for_config', e.target.value)} style={inputStyle} placeholder='Wait config JSON (e.g. {"approver":"admin@co.com"})' />
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button onClick={() => setView('list')} style={btnStyle('#6b7280')}>Cancel</button>
          <button onClick={saveAgent} style={btnStyle(ACCENT)} disabled={!name || phases.length === 0}>Save Agent</button>
        </div>
      </div>
    );
  }

  function renderConversations() {
    if (conversations.length === 0) {
      return (
        <div style={emptyStyle}>
          <p style={{ margin: 0, color: '#6b7280' }}>No conversations found.</p>
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {conversations.map(conv => {
          const agent = agents.find(a => a.id === conv.agent_id);
          return (
            <div key={conv.id} style={{ ...cardStyle, cursor: 'pointer' }} onClick={() => viewConversationDetail(conv.id)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={badgeStyle(STATUS_COLORS[conv.phase_status] || '#6b7280')}>{conv.phase_status}</span>
                  <span style={{ fontWeight: 600 }}>{agent?.name || conv.agent_id.slice(0, 8)}</span>
                  <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>Phase: {conv.current_phase.slice(0, 8)}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                    {conv.history.length} step{conv.history.length !== 1 ? 's' : ''}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                    {new Date(conv.started_at).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  function renderDetail() {
    if (!selectedConv) return null;
    const conv = selectedConv;
    const agent = agents.find(a => a.id === conv.agent_id);
    const isPending = conv.phase_status === 'approval_pending';
    const isActive = ['active', 'waiting', 'approval_pending'].includes(conv.phase_status);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div>
              <h2 style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0 }}>{agent?.name || 'Unknown Agent'}</h2>
              <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontFamily: 'monospace' }}>{conv.id}</span>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {isPending && (
                <>
                  <button onClick={() => approveConversation(conv.id)} style={btnStyle('#10b981')}>Approve</button>
                  <button onClick={() => rejectConversation(conv.id)} style={btnStyle('#ef4444')}>Reject</button>
                </>
              )}
              {isActive && (
                <button onClick={() => cancelConversation(conv.id)} style={btnStyle('#6b7280')}>Cancel</button>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
            <div>
              <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Status</span>
              <div><span style={badgeStyle(STATUS_COLORS[conv.phase_status] || '#6b7280')}>{conv.phase_status}</span></div>
            </div>
            <div>
              <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Started</span>
              <div style={{ fontSize: '0.875rem' }}>{new Date(conv.started_at).toLocaleString()}</div>
            </div>
            <div>
              <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Expires</span>
              <div style={{ fontSize: '0.875rem' }}>{new Date(conv.expires_at).toLocaleString()}</div>
            </div>
            {conv.approval_requested_from && (
              <div>
                <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Approval From</span>
                <div style={{ fontSize: '0.875rem' }}>{conv.approval_requested_from}</div>
              </div>
            )}
          </div>
        </div>

        {/* Timeline */}
        <div style={cardStyle}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 1rem' }}>Timeline</h3>
          <div style={{ position: 'relative', paddingLeft: '1.5rem' }}>
            {conv.history.map((entry, i) => {
              const isLast = i === conv.history.length - 1;
              const isCurrent = isLast && isActive;
              return (
                <div key={i} style={{ position: 'relative', paddingBottom: isLast ? 0 : '1.25rem', marginLeft: '0.5rem', borderLeft: isLast ? 'none' : '2px solid #e5e7eb' }}>
                  <div style={{
                    position: 'absolute',
                    left: '-7px',
                    top: 2,
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: isCurrent ? ACCENT : '#d1d5db',
                    border: isCurrent ? `2px solid ${ACCENT}` : '2px solid #e5e7eb',
                  }} />
                  <div style={{ paddingLeft: '1rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{entry.action}</span>
                      <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{new Date(entry.timestamp).toLocaleString()}</span>
                    </div>
                    <p style={{ fontSize: '0.8125rem', color: '#6b7280', margin: '0.125rem 0 0' }}>{entry.result}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Context data */}
        {Object.keys(conv.context).length > 0 && (
          <div style={cardStyle}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 0.75rem' }}>Context Data</h3>
            <pre style={{ background: '#f3f4f6', padding: '0.75rem', borderRadius: '0.375rem', fontSize: '0.8125rem', overflow: 'auto', margin: 0 }}>
              {JSON.stringify(conv.context, null, 2)}
            </pre>
          </div>
        )}
      </div>
    );
  }
}

// Styles
const cardStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: '0.5rem',
  padding: '1rem 1.25rem',
};

const emptyStyle: React.CSSProperties = {
  ...cardStyle,
  textAlign: 'center',
  padding: '3rem',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: '#374151',
  marginBottom: '0.25rem',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  border: '1px solid #d1d5db',
  borderRadius: '0.375rem',
  fontSize: '0.875rem',
  outline: 'none',
  boxSizing: 'border-box',
};

function btnStyle(bg: string): React.CSSProperties {
  return {
    padding: '0.5rem 1rem',
    backgroundColor: bg,
    color: '#fff',
    border: 'none',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'pointer',
  };
}

function btnSmall(bg: string): React.CSSProperties {
  return {
    padding: '0.25rem 0.5rem',
    backgroundColor: bg,
    color: '#fff',
    border: 'none',
    borderRadius: '0.25rem',
    fontSize: '0.75rem',
    cursor: 'pointer',
  };
}

function badgeStyle(color: string): React.CSSProperties {
  return {
    display: 'inline-block',
    padding: '0.125rem 0.5rem',
    backgroundColor: color + '18',
    color,
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: 600,
  };
}
